import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Select from 'react-select';
import { useNotification } from '../../contexts/NotificationContext';
import { propertyService, brokerService, categoryService, leadService, canalProService } from '../../services/api';
import Modal from '../../components/common/Modal';
import ImageGalleryWithCover from '../../components/property/ImageGalleryWithCover';
import VideoManager from '../../components/property/VideoManager';
import { channelStatusLabel, syncStatusLabel } from '../../utils/propertyStatus';

interface PricingInfo {
  business_type: 'SALE' | 'RENTAL';
  price: number;
  monthly_condo_fee?: number;
  yearly_iptu?: number;
  rental_total_price?: number;
  rental_period?: string;
  rental_warranties?: string[];
}

interface Property {
  id?: string;  // UUID stored as string
  title: string;
  description: string;
  type: string;
  transaction_type: string;
  status: string;
  commercial_status: string;
  site_publication_status: string;
  price: number;
  pricing_infos: PricingInfo[];
  monthly_condo_fee?: number;
  yearly_iptu?: number;
  iptu?: number;
  iptu_period?: 'MONTHLY' | 'YEARLY';
  location: string;
  condominium_name?: string;
  address?: {
    street: string;
    number: string;
    neighborhood: string;
    city: string;
    state: string;
    zipCode: string;
    complement?: string;
  };
  bedrooms: number;
  suites?: number;
  bathrooms: number;
  parking_spaces?: number;
  area: number;
  total_areas?: number;
  usable_areas?: number;
  unit_floor?: number;
  floors?: number;
  buildings?: number;
  units_on_the_floor?: number;
  amenities?: string[];
  delivered_at?: string;
  show_price?: boolean;
  features: string[];
  images: string[];
  cover_image?: string;
  videos?: any[];
  featured_video_id?: string;
  captured_by_id?: number;
  managed_by_id?: number;
  owner_id?: number;
  category_ids?: number[];
  categories?: Array<{
    id: number;
    name: string;
    slug: string;
    color: string;
    icon?: string;
  }>;
  internal_observations?: string;
  has_keys?: boolean;
  keys_location?: string;
}

type ChannelDesiredStatus = 'ACTIVE' | 'INACTIVE' | 'DRAFT';

interface ChannelPublication {
  id: string;
  property_id: string;
  external_id?: string;
  desired_status: string;
  actual_status?: string;
  sync_status: string;
  last_error?: string;
  last_synced_at?: string;
}

interface SyncOverview {
  settings: {
    sync_enabled: boolean;
    outbound_enabled: boolean;
  };
  quota: {
    limit: number;
    active: number;
    reserved: number;
    available: number;
  };
}

const commercialStatusFromLegacy = (status?: string) => {
  const mapping: Record<string, string> = {
    disponivel: 'available',
    reservado: 'reserved',
    vendido: 'sold',
    alugado: 'rented',
    cancelado: 'withdrawn'
  };
  return mapping[status || ''] || 'unknown';
};

const legacyStatusFromCommercial = (commercialStatus: string) => {
  const mapping: Record<string, string> = {
    available: 'disponivel',
    reserved: 'reservado',
    sold: 'vendido',
    rented: 'alugado',
    withdrawn: 'cancelado',
    unknown: 'indisponivel'
  };
  return mapping[commercialStatus] || 'indisponivel';
};

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: #1f2937;
  margin: 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Section = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled.h2`
  color: #374151;
  margin: 0 0 1.5rem 0;
  font-size: 1.25rem;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 0.5rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
`;

const PublicationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1rem;
`;

const PublicationCard = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1rem;
  background: #f9fafb;
`;

const CardTitle = styled.h3`
  margin: 0 0 0.35rem;
  color: #1f2937;
  font-size: 1rem;
`;

const HelpText = styled.p<{ $tone?: 'normal' | 'warning' | 'error' }>`
  margin: 0.5rem 0 0;
  color: ${({ $tone }) => $tone === 'error' ? '#b91c1c' : $tone === 'warning' ? '#92400e' : '#6b7280'};
  font-size: 0.8rem;
  line-height: 1.4;
`;

const StatusSummary = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

const StatusPill = styled.span<{ $tone?: 'success' | 'warning' | 'error' | 'neutral' }>`
  padding: 0.25rem 0.55rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${({ $tone }) => $tone === 'success' ? '#d1fae5' : $tone === 'warning' ? '#fef3c7' : $tone === 'error' ? '#fee2e2' : '#e5e7eb'};
  color: ${({ $tone }) => $tone === 'success' ? '#065f46' : $tone === 'warning' ? '#92400e' : $tone === 'error' ? '#991b1b' : '#374151'};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 1rem;
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }
`;

const NativeSelect = styled.select`
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 1rem;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }
`;

const LocationContainer = styled.div`
  position: relative;
`;

const LocationInput = styled(Input)`
  padding-right: 3rem;
`;

const LocationButton = styled.button`
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  
  &:hover {
    background: #1d4ed8;
  }
`;

const FeaturesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const FeatureTag = styled.div`
  background: #e5e7eb;
  color: #374151;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const RemoveFeature = styled.button`
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  font-size: 1rem;
  
  &:hover {
    color: #ef4444;
  }
`;

const AddFeatureContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const AddFeatureInput = styled(Input)`
  flex: 1;
`;

const AddFeatureButton = styled.button`
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.75rem 1rem;
  cursor: pointer;
  font-size: 0.875rem;
  
  &:hover {
    background: #1d4ed8;
  }
`;

const CategoriesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 0.5rem;
`;

const CategoryOption = styled.div<{ selected: boolean; color: string }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 2px solid ${props => props.selected ? props.color : '#e5e7eb'};
  background: ${props => props.selected ? props.color + '20' : 'white'};
  border-radius: 20px;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s;
  
  &:hover {
    border-color: ${props => props.color};
    background: ${props => props.color + '10'};
  }
`;

const CategoryIcon = styled.span`
  font-size: 1rem;
`;

const CategoryName = styled.span`
  color: #374151;
  font-weight: 500;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'success' | 'danger' }>`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  
  ${props => {
    switch (props.$variant) {
      case 'primary':
        return `
          background: #2563eb;
          color: white;
          
          &:hover {
            background: #1d4ed8;
          }
        `;
      case 'success':
        return `
          background: #10b981;
          color: white;
          
          &:hover {
            background: #059669;
          }
        `;
      case 'danger':
        return `
          background: #ef4444;
          color: white;
          
          &:hover {
            background: #dc2626;
          }
        `;
      default:
        return `
          background: #6b7280;
          color: white;
          
          &:hover {
            background: #4b5563;
          }
        `;
    }
  }}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ApprovalButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 1rem;
  background: #fef3c7;
  border-radius: 8px;
  border: 1px solid #fcd34d;
`;

const ApprovalMessage = styled.p`
  flex: 1;
  margin: 0;
  color: #92400e;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
`;

const StyledSelect = styled(Select)`
  .react-select__control {
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    min-height: 42px;
    box-shadow: none;
    
    &:hover {
      border-color: #d1d5db;
    }
    
    &--is-focused {
      border-color: #2563eb;
      box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
    }
  }
  
  .react-select__value-container {
    padding: 2px 8px;
  }
  
  .react-select__indicator-separator {
    display: none;
  }
  
  .react-select__menu {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    border: 1px solid #e5e7eb;
  }
  
  .react-select__option {
    &--is-selected {
      background-color: #2563eb;
    }
    
    &--is-focused {
      background-color: #dbeafe;
    }
  }
`;

const AdminPropertyForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(false);
  const [imagesUploading, setImagesUploading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [newFeature, setNewFeature] = useState('');
  const [priceDisplay, setPriceDisplay] = useState('');
  const [rentalPriceDisplay, setRentalPriceDisplay] = useState('');
  const [condoFeeDisplay, setCondoFeeDisplay] = useState('');
  const [iptuDisplay, setIptuDisplay] = useState('');
  const [areaDisplay, setAreaDisplay] = useState('');
  const [totalAreaDisplay, setTotalAreaDisplay] = useState('');
  const [brokers, setBrokers] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [leads, setLeads] = useState<any[]>([]);
  const [loadingBrokers, setLoadingBrokers] = useState(false);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [channelLoading, setChannelLoading] = useState(true);
  const [channelConfigured, setChannelConfigured] = useState(false);
  const [channelLoadError, setChannelLoadError] = useState('');
  const [channelPublication, setChannelPublication] = useState<ChannelPublication | null>(null);
  const [syncOverview, setSyncOverview] = useState<SyncOverview | null>(null);
  const [channelDesiredStatus, setChannelDesiredStatus] = useState<ChannelDesiredStatus>('DRAFT');
  const [originalChannelDesiredStatus, setOriginalChannelDesiredStatus] = useState<ChannelDesiredStatus>('DRAFT');
  const [zipCodeLookupValid, setZipCodeLookupValid] = useState<boolean | null>(null);

  const [property, setProperty] = useState<Property>({
    title: '',
    description: '',
    type: 'casa',
    transaction_type: 'venda',
    status: 'disponivel',
    commercial_status: 'available',
    site_publication_status: 'published',
    price: 0,
    pricing_infos: [{ business_type: 'SALE', price: 0 }],
    monthly_condo_fee: 0,
    yearly_iptu: 0,
    iptu: 0,
    location: '',
    condominium_name: '',
    address: {
      street: '',
      number: '',
      neighborhood: '',
      city: '',
      state: '',
      zipCode: '',
      complement: ''
    },
    bedrooms: 0,
    suites: 0,
    bathrooms: 0,
    parking_spaces: 0,
    area: 0,
    total_areas: undefined,
    usable_areas: undefined,
    unit_floor: undefined,
    floors: undefined,
    buildings: undefined,
    units_on_the_floor: undefined,
    amenities: [],
    show_price: true,
    features: [],
    images: [],
    cover_image: '',
    videos: [],
    featured_video_id: undefined,
    captured_by_id: undefined,
    managed_by_id: undefined,
    owner_id: undefined,
    category_ids: []
  });

  useEffect(() => {
    loadBrokers();
    loadCategories();
    loadLeads();
    if (id) {
      loadProperty();
    }
    loadCanalProContext();
  }, [id]);

  // Atualizar endereço completo quando os campos mudarem
  useEffect(() => {
    if (property.address) {
      const { street, number, neighborhood, city, state, complement } = property.address;
      
      if (street || neighborhood || city) {
        let fullAddress = '';
        
        if (street) fullAddress += street;
        if (number) fullAddress += `, ${number}`;
        if (complement) fullAddress += `, ${complement}`;
        if (neighborhood) fullAddress += `, ${neighborhood}`;
        if (city) fullAddress += `, ${city}`;
        if (state) fullAddress += ` - ${state}`;
        
        if (fullAddress !== property.location) {
          setProperty(prev => ({
            ...prev,
            location: fullAddress
          }));
        }
      }
    }
  }, [property.address]);

  const loadBrokers = async () => {
    try {
      setLoadingBrokers(true);
      const brokersData = await brokerService.getActive();
      setBrokers(brokersData);
    } catch (error) {
      console.error('Erro ao carregar corretores:', error);
    } finally {
      setLoadingBrokers(false);
    }
  };

  const loadCategories = async () => {
    try {
      const categoriesData = await categoryService.getPublic();
      setCategories(categoriesData);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  };

  const loadLeads = async () => {
    try {
      setLoadingLeads(true);
      const leadsData = await leadService.getAll();
      setLeads(leadsData);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
    } finally {
      setLoadingLeads(false);
    }
  };

  const loadProperty = async () => {
    try {
      setLoading(true);
      const data = await propertyService.getById(id!);
      const pricingInfos: PricingInfo[] = data.pricing_infos?.length
        ? data.pricing_infos
        : [{
            business_type: data.transaction_type === 'aluguel' ? 'RENTAL' : 'SALE',
            price: data.price || 0,
            monthly_condo_fee: data.monthly_condo_fee,
            yearly_iptu: data.yearly_iptu,
          }];
      setProperty({
        ...data,
        pricing_infos: pricingInfos,
        commercial_status: data.commercial_status || commercialStatusFromLegacy(data.status),
        site_publication_status: data.site_publication_status || (data.status === 'disponivel' ? 'published' : 'hidden'),
        features: data.features || [],
        images: data.images || [],
        cover_image: data.cover_image || '',
        category_ids: data.categories ? data.categories.map((cat: any) => cat.id) : [],
        address: data.address || {
          street: '',
          number: '',
          neighborhood: '',
          city: '',
          state: '',
          zipCode: '',
          complement: ''
        }
      });
      const loadedZipCode = String(data.address?.zipCode || '').replace(/\D/g, '');
      setZipCodeLookupValid(loadedZipCode.length === 8 ? true : null);
      
      // Atualizar displays formatados
      const salePrice = pricingInfos.find(item => item.business_type === 'SALE')?.price;
      const rentalPrice = pricingInfos.find(item => item.business_type === 'RENTAL')?.price;
      setPriceDisplay(salePrice != null ? formatCurrency(salePrice) : '');
      setRentalPriceDisplay(rentalPrice != null ? formatCurrency(rentalPrice) : '');
      
      if (data.monthly_condo_fee) {
        setCondoFeeDisplay(new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(data.monthly_condo_fee));
      }
      
      if (data.yearly_iptu || data.iptu) {
        const iptuValue = data.yearly_iptu || data.iptu || 0;
        setIptuDisplay(new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(iptuValue));
      }
      
      const formattedArea = new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(data.area || 0);
      
      setAreaDisplay(data.area ? `${formattedArea} m²` : '');
      setTotalAreaDisplay(data.total_areas
        ? `${new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(data.total_areas)} m²`
        : '');
    } catch (error) {
      console.error('Erro ao carregar propriedade:', error);
      showNotification('Erro ao carregar propriedade', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadCanalProContext = async () => {
    try {
      setChannelLoading(true);
      setChannelLoadError('');
      const overview = await canalProService.getSyncOverview();
      setSyncOverview(overview);
      setChannelConfigured(true);

      if (id) {
        const publication = await canalProService.getPropertyPublication(id);
        setChannelPublication(publication);
        const rawDesiredStatus = publication?.desired_status || 'DRAFT';
        const desiredStatus: ChannelDesiredStatus = ['ACTIVE', 'INACTIVE', 'DRAFT'].includes(rawDesiredStatus)
          ? rawDesiredStatus as ChannelDesiredStatus
          : 'DRAFT';
        setChannelDesiredStatus(desiredStatus);
        setOriginalChannelDesiredStatus(desiredStatus);
      }
    } catch (error: any) {
      if (error.response?.status === 404) {
        setChannelConfigured(false);
      } else {
        console.error('Erro ao carregar publicação no Canal Pro:', error);
        setChannelLoadError('Não foi possível consultar o Canal Pro agora. Os dados do imóvel ainda podem ser salvos.');
      }
    } finally {
      setChannelLoading(false);
    }
  };

  const handleApprove = async () => {
    try {
      setLoading(true);
      await propertyService.approve(id!);
      showNotification('Imóvel aprovado com sucesso!', 'success');
      navigate('/admin/properties');
    } catch (error: any) {
      console.error('Erro ao aprovar imóvel:', error);
      showNotification(
        error.response?.data?.detail || 'Erro ao aprovar imóvel',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    try {
      setLoading(true);
      await propertyService.reject(id!);
      showNotification('Imóvel rejeitado com sucesso!', 'success');
      navigate('/admin/properties');
    } catch (error: any) {
      console.error('Erro ao rejeitar imóvel:', error);
      showNotification(
        error.response?.data?.detail || 'Erro ao rejeitar imóvel',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (imagesUploading) {
      showNotification('Aguarde o envio das imagens antes de salvar o imóvel.', 'error');
      return;
    }
    
    try {
      if (!property.pricing_infos.length || property.pricing_infos.some(item => item.price <= 0)) {
        showNotification('Informe um preço válido para cada finalidade selecionada.', 'error');
        return;
      }
      const zipCode = String(property.address?.zipCode || '').replace(/\D/g, '');
      if (zipCode.length !== 8 || zipCodeLookupValid === false) {
        showNotification(
          'Informe um CEP válido com 8 dígitos. O Canal Pro exige CEP até para rascunhos.',
          'error'
        );
        return;
      }
      setLoading(true);
      
      const savedProperty = id
        ? await propertyService.update(id, property)
        : await propertyService.create(property);
      const propertyId = id || savedProperty.id;
      const channelStatusChanged = id
        ? channelDesiredStatus !== originalChannelDesiredStatus
        : channelDesiredStatus !== 'DRAFT';

      if (channelConfigured && channelStatusChanged) {
        try {
          await canalProService.setPropertyChannelStatus(propertyId, channelDesiredStatus);
        } catch (channelError: any) {
          const detail = channelError.response?.data?.detail || 'não foi possível atualizar o Canal Pro';
          showNotification(`Imóvel salvo, mas o Canal Pro não foi alterado: ${detail}`, 'error');
          if (!id) {
            navigate(`/admin/properties/edit/${propertyId}`, { replace: true });
          } else {
            await loadCanalProContext();
          }
          return;
        }
      }

      showNotification(
        id ? 'Imóvel e publicações atualizados com sucesso!' : 'Imóvel criado com sucesso!',
        'success'
      );
      navigate('/admin/properties');
    } catch (error: any) {
      console.error('Erro ao salvar propriedade:', error);
      showNotification(error.response?.data?.detail || 'Erro ao salvar propriedade', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof Property, value: any) => {
    setProperty(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCommercialStatusChange = (commercialStatus: string) => {
    setProperty(prev => ({
      ...prev,
      commercial_status: commercialStatus,
      status: legacyStatusFromCommercial(commercialStatus)
    }));
  };

  const formatCurrency = (value: number) => new Intl.NumberFormat('pt-BR', {
    style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2
  }).format(value);

  const updatePricingInfo = (businessType: 'SALE' | 'RENTAL', price: number) => {
    setProperty(prev => {
      const existing = prev.pricing_infos.filter(item => item.business_type !== businessType);
      const pricingInfos = [...existing, {
        business_type: businessType,
        price,
        monthly_condo_fee: prev.monthly_condo_fee,
        yearly_iptu: prev.yearly_iptu,
      }].sort((a, b) => a.business_type === 'SALE' ? -1 : b.business_type === 'SALE' ? 1 : 0);
      const hasSale = pricingInfos.some(item => item.business_type === 'SALE');
      const hasRental = pricingInfos.some(item => item.business_type === 'RENTAL');
      return {
        ...prev,
        pricing_infos: pricingInfos,
        transaction_type: hasSale && hasRental ? 'venda_aluguel' : hasRental ? 'aluguel' : 'venda',
        price: pricingInfos.find(item => item.business_type === 'SALE')?.price
          ?? pricingInfos.find(item => item.business_type === 'RENTAL')?.price
          ?? 0,
      };
    });
  };

  const toggleBusinessType = (businessType: 'SALE' | 'RENTAL', enabled: boolean) => {
    if (enabled) {
      updatePricingInfo(businessType, 0);
      if (businessType === 'SALE') setPriceDisplay('');
      else setRentalPriceDisplay('');
      return;
    }
    if (property.pricing_infos.length <= 1) {
      showNotification('O imóvel precisa de pelo menos uma finalidade.', 'error');
      return;
    }
    // Limpar o display junto evita reexibir o preco antigo de uma modalidade
    // que foi removida e depois remarcada.
    if (businessType === 'SALE') setPriceDisplay('');
    else setRentalPriceDisplay('');
    setProperty(prev => {
      const pricingInfos = prev.pricing_infos.filter(item => item.business_type !== businessType);
      if (!pricingInfos.length) return prev;
      const hasSale = pricingInfos.some(item => item.business_type === 'SALE');
      return {
        ...prev,
        pricing_infos: pricingInfos,
        transaction_type: hasSale ? 'venda' : 'aluguel',
        price: pricingInfos[0].price,
      };
    });
  };

  const handlePriceChange = (businessType: 'SALE' | 'RENTAL', value: string) => {
    const numbers = value.replace(/\D/g, '');
    const numberValue = numbers ? parseFloat(numbers) / 100 : 0;
    updatePricingInfo(businessType, numberValue);
    if (businessType === 'SALE') setPriceDisplay(numbers ? formatCurrency(numberValue) : '');
    else setRentalPriceDisplay(numbers ? formatCurrency(numberValue) : '');
  };

  const handleCondoFeeChange = (value: string) => {
    // Remove tudo que não é número
    const numbers = value.replace(/\D/g, '');
    
    if (numbers === '') {
      setProperty(prev => ({
        ...prev,
        monthly_condo_fee: 0,
        pricing_infos: prev.pricing_infos.map(item => ({
          ...item,
          monthly_condo_fee: undefined,
        })),
      }));
      setCondoFeeDisplay('');
      return;
    }
    
    // Converte para número considerando centavos
    const numberValue = parseFloat(numbers) / 100;
    
    // Atualiza o estado com o valor numérico
    setProperty(prev => ({
      ...prev,
      monthly_condo_fee: numberValue,
      pricing_infos: prev.pricing_infos.map(item => ({
        ...item,
        monthly_condo_fee: numberValue,
      })),
    }));
    
    // Atualiza o display formatado
    setCondoFeeDisplay(new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numberValue));
  };

  const handleIptuChange = (value: string) => {
    // Remove tudo que não é número
    const numbers = value.replace(/\D/g, '');
    
    if (numbers === '') {
      setProperty(prev => ({
        ...prev,
        iptu: 0,
        yearly_iptu: undefined,
        pricing_infos: prev.pricing_infos.map(item => ({
          ...item,
          yearly_iptu: undefined,
        })),
      }));
      setIptuDisplay('');
      return;
    }
    
    // Converte para número considerando centavos
    const numberValue = parseFloat(numbers) / 100;
    
    // Atualiza o estado com o valor numérico
    setProperty(prev => ({
      ...prev,
      iptu: prev.iptu_period === 'YEARLY' ? undefined : numberValue,
      yearly_iptu: prev.iptu_period === 'YEARLY' ? numberValue : undefined,
      pricing_infos: prev.pricing_infos.map(item => ({
        ...item,
        yearly_iptu: prev.iptu_period === 'YEARLY' ? numberValue : undefined,
      })),
    }));
    
    // Atualiza o display formatado
    setIptuDisplay(new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numberValue));
  };

  const handleTotalAreaChange = (value: string) => {
    const cleanValue = value.replace(/[^\d,\.]/g, '');
    if (cleanValue === '') {
      // Em branco vale "nao informada": no Canal Pro isso e diferente de zero,
      // entao nao se manda 0 para nao afirmar que o imovel tem area total zero.
      handleInputChange('total_areas', undefined);
      setTotalAreaDisplay('');
      return;
    }
    const numberValue = parseFloat(cleanValue.replace(',', '.')) || 0;
    handleInputChange('total_areas', numberValue > 0 ? numberValue : undefined);
    const formatted = new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 0, maximumFractionDigits: 2,
    }).format(numberValue);
    setTotalAreaDisplay(numberValue > 0 ? `${formatted} m²` : '');
  };

  const handleAreaChange = (value: string) => {
    // Remove tudo que não é número ou vírgula/ponto
    const cleanValue = value.replace(/[^\d,\.]/g, '');
    
    if (cleanValue === '') {
      handleInputChange('area', 0);
      setAreaDisplay('');
      return;
    }
    
    // Converte vírgula para ponto e depois para número
    const numberValue = parseFloat(cleanValue.replace(',', '.')) || 0;
    
    // Atualiza o estado com o valor numérico
    handleInputChange('area', numberValue);
    
    // Atualiza o display formatado
    const formattedArea = new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(numberValue);
    
    setAreaDisplay(numberValue > 0 ? `${formattedArea} m²` : '');
  };

  const handleZipCodeSearch = async (zipCode: string) => {
    const cleanZipCode = zipCode.replace(/\D/g, '');
    
    if (cleanZipCode.length !== 8) {
      setZipCodeLookupValid(false);
      return;
    }
    setZipCodeLookupValid(null);
    if (cleanZipCode.length === 8) {
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cleanZipCode}/json/`);
        const data = await response.json();
        
        if (!data.erro) {
          setZipCodeLookupValid(true);
          handleInputChange('address', {
            ...property.address!,
            street: data.logradouro || '',
            neighborhood: data.bairro || '',
            city: data.localidade || '',
            state: data.uf || '',
            zipCode: cleanZipCode
          });
          
          // Atualizar location com endereço completo
          const fullAddress = `${data.logradouro}, ${data.bairro}, ${data.localidade} - ${data.uf}`;
          handleInputChange('location', fullAddress);
        } else {
          setZipCodeLookupValid(false);
          showNotification('CEP não encontrado. Confira os 8 dígitos informados.', 'error');
        }
      } catch (error) {
        console.error('Erro ao buscar CEP:', error);
        setZipCodeLookupValid(null);
      }
    }
  };

  const handleLocationSearch = () => {
    if (property.location) {
      const encodedLocation = encodeURIComponent(property.location);
      window.open(`https://www.google.com/maps/search/${encodedLocation}`, '_blank');
    }
  };

  const formatZipCode = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    return numbers.replace(/(\d{5})(\d{3})/, '$1-$2');
  };

  const addFeature = () => {
    if (newFeature.trim() && !property.features.includes(newFeature.trim())) {
      handleInputChange('features', [...property.features, newFeature.trim()]);
      setNewFeature('');
    }
  };

  const removeFeature = (featureToRemove: string) => {
    handleInputChange('features', property.features.filter(f => f !== featureToRemove));
  };

  const toggleCategory = (categoryId: number) => {
    const currentIds = property.category_ids || [];
    const newIds = currentIds.includes(categoryId)
      ? currentIds.filter(id => id !== categoryId)
      : [...currentIds, categoryId];
    
    handleInputChange('category_ids', newIds);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addFeature();
    }
  };

  // Função para formatar opções com mais informações
  const formatBrokerOption = (broker: any) => {
    return (
      <div>
        <div style={{ fontWeight: 500 }}>{broker.name}</div>
        <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
          CRECI: {broker.creci} | {broker.email}
        </div>
      </div>
    );
  };

  const formatLeadOption = (lead: any) => {
    const clientTypeMap: { [key: string]: string } = {
      'buyer': 'Comprador',
      'renter': 'Locatário',
      'owner': 'Proprietário',
      'seller': 'Vendedor',
      'broker': 'Corretor',
      'partner_broker': 'Corretor Parceiro',
      'doorman': 'Porteiro',
      'investor': 'Investidor',
      'developer': 'Incorporadora',
      'construction_company': 'Construtora',
      'referral': 'Indicação',
      'other': 'Outro'
    };
    
    const clientTypeKey = lead.client_type || lead.lead_type || 'other';
    const clientType = clientTypeMap[clientTypeKey] || clientTypeKey;
    
    return (
      <div>
        <div style={{ fontWeight: 500 }}>{lead.name}</div>
        <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
          {lead.email} | {lead.phone || 'Sem telefone'} | {clientType}
        </div>
      </div>
    );
  };

  if (loading && id) {
    return (
      <Container>
        <div>Carregando propriedade...</div>
      </Container>
    );
  }

  const draftStatusUnavailable = Boolean(
    channelPublication?.external_id &&
    String(channelPublication.actual_status || '').toUpperCase() !== 'DRAFT'
  );

  return (
    <Container>
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          navigate('/admin/properties');
        }}
        title={id ? 'Editar Propriedade' : 'Nova Propriedade'}
        size="xl"
        closeOnOverlayClick={false}
        showCloseConfirmation={true}
      >
        <Form onSubmit={handleSubmit}>
          {/* Botões de Aprovação/Rejeição */}
          {id && property.status === 'pending_approval' && (
            <ApprovalButtonGroup>
              <ApprovalMessage>
                ⚠️ Este imóvel está pendente de aprovação
              </ApprovalMessage>
              <Button
                type="button"
                $variant="success"
                onClick={handleApprove}
                disabled={loading}
              >
                Aprovar Imóvel
              </Button>
              <Button
                type="button"
                $variant="danger"
                onClick={handleReject}
                disabled={loading}
              >
                Rejeitar Imóvel
              </Button>
            </ApprovalButtonGroup>
          )}

          <Section>
            <SectionTitle>Publicação e disponibilidade</SectionTitle>
            <PublicationGrid>
              <PublicationCard>
                <CardTitle>Situação comercial</CardTitle>
                <NativeSelect
                  value={property.commercial_status}
                  onChange={(e) => handleCommercialStatusChange(e.target.value)}
                >
                  <option value="available">Disponível</option>
                  <option value="reserved">Reservado</option>
                  <option value="sold">Vendido</option>
                  <option value="rented">Alugado</option>
                  <option value="withdrawn">Retirado</option>
                  <option value="unknown">A revisar</option>
                </NativeSelect>
                <HelpText>Representa a situação real do imóvel e não depende da publicação em nenhum canal.</HelpText>
              </PublicationCard>

              <PublicationCard>
                <CardTitle>Site</CardTitle>
                <NativeSelect
                  value={property.site_publication_status}
                  onChange={(e) => handleInputChange('site_publication_status', e.target.value)}
                >
                  <option value="published">Publicado no site</option>
                  <option value="hidden">Oculto do site</option>
                  <option value="draft">Rascunho</option>
                </NativeSelect>
                <HelpText>Controla a vitrine do site sem ativar ou desativar o anúncio no Canal Pro.</HelpText>
              </PublicationCard>

              <PublicationCard>
                <CardTitle>Canal Pro</CardTitle>
                {channelLoading ? (
                  <HelpText>Consultando publicação e limite do Canal Pro...</HelpText>
                ) : channelLoadError ? (
                  <HelpText $tone="error">{channelLoadError}</HelpText>
                ) : !channelConfigured ? (
                  <HelpText>Este tenant não possui uma integração Canal Pro configurada.</HelpText>
                ) : (
                  <>
                    <NativeSelect
                      value={channelDesiredStatus}
                      onChange={(e) => setChannelDesiredStatus(e.target.value as ChannelDesiredStatus)}
                      disabled={!syncOverview?.settings.sync_enabled || !syncOverview?.settings.outbound_enabled}
                    >
                      <option
                        value="ACTIVE"
                        disabled={Boolean(
                          syncOverview &&
                          syncOverview.quota.available <= 0 &&
                          channelPublication?.actual_status !== 'ACTIVE' &&
                          originalChannelDesiredStatus !== 'ACTIVE'
                        )}
                      >Ativo no Canal Pro</option>
                      <option value="INACTIVE">Inativo no Canal Pro</option>
                      <option value="DRAFT" disabled={draftStatusUnavailable}>
                        Rascunho no Canal Pro
                      </option>
                    </NativeSelect>
                    <StatusSummary>
                      <StatusPill $tone={channelPublication?.actual_status === 'ACTIVE' ? 'success' : 'neutral'}>
                        Confirmado: {channelPublication?.actual_status
                          ? channelStatusLabel(channelPublication.actual_status)
                          : 'ainda não enviado'}
                      </StatusPill>
                      <StatusPill $tone={channelPublication?.sync_status === 'failed' ? 'error' : channelPublication?.sync_status === 'synced' ? 'success' : 'warning'}>
                        Sincronização: {syncStatusLabel(channelPublication?.sync_status) || 'novo'}
                      </StatusPill>
                    </StatusSummary>
                    {syncOverview && (
                      <HelpText>
                        Cota: {syncOverview.quota.active} ativos + {syncOverview.quota.reserved} reservados de {syncOverview.quota.limit}. {syncOverview.quota.available} vaga(s) livre(s).
                      </HelpText>
                    )}
                    {(!syncOverview?.settings.sync_enabled || !syncOverview?.settings.outbound_enabled) && (
                      <HelpText $tone="warning">O envio ao Canal Pro está pausado nas configurações da integração.</HelpText>
                    )}
                    {draftStatusUnavailable && (
                      <HelpText $tone="warning">
                        O Canal Pro só aceita rascunho na criação. Para retirar um anúncio já criado, selecione “Inativo no Canal Pro”.
                      </HelpText>
                    )}
                    {channelPublication?.last_error && (
                      <HelpText $tone="error">Último erro: {channelPublication.last_error}</HelpText>
                    )}
                  </>
                )}
              </PublicationCard>
            </PublicationGrid>
            <HelpText>As três configurações são independentes e serão aplicadas ao salvar o imóvel.</HelpText>
          </Section>

          {/* Informações Básicas */}
          <Section>
            <SectionTitle>Informações Básicas</SectionTitle>
            
            <FormGroup>
              <Label>Título</Label>
              <Input
                type="text"
                value={property.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>Descrição</Label>
              <TextArea
                value={property.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Descreva detalhadamente a propriedade..."
                required
              />
            </FormGroup>

            <FormRow>
              <FormGroup>
                <Label>Tipo</Label>
                <NativeSelect
                  value={property.type}
                  onChange={(e) => handleInputChange('type', e.target.value)}
                >
                  <option value="casa">Casa</option>
                  <option value="apartamento">Apartamento</option>
                  <option value="terreno">Terreno</option>
                  <option value="comercial">Comercial</option>
                  <option value="chacara">Chácara</option>
                  <option value="galpao">Galpão</option>
                </NativeSelect>
              </FormGroup>

              <FormGroup>
                <Label>Finalidades</Label>
                <label>
                  <input
                    type="checkbox"
                    checked={property.pricing_infos.some(item => item.business_type === 'SALE')}
                    onChange={(e) => toggleBusinessType('SALE', e.target.checked)}
                  />{' '}Venda
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={property.pricing_infos.some(item => item.business_type === 'RENTAL')}
                    onChange={(e) => toggleBusinessType('RENTAL', e.target.checked)}
                  />{' '}Locação
                </label>
                <HelpText>Selecione uma ou as duas finalidades. Cada uma mantém seu próprio preço.</HelpText>
              </FormGroup>

            </FormRow>
          </Section>

          {/* Corretores */}
          <Section>
            <SectionTitle>Corretores</SectionTitle>
            
            <FormRow>
              <FormGroup>
                <Label>Captado por</Label>
                <StyledSelect
                  classNamePrefix="react-select"
                  value={brokers.find(broker => broker.id === property.captured_by_id) || null}
                  onChange={(selectedOption: any) => handleInputChange('captured_by_id', selectedOption?.id || undefined)}
                  options={brokers}
                  getOptionLabel={(option: any) => `${option.name} - ${option.creci}`}
                  getOptionValue={(option: any) => option.id.toString()}
                  formatOptionLabel={formatBrokerOption}
                  placeholder="Buscar corretor..."
                  isClearable
                  isSearchable
                  isLoading={loadingBrokers}
                  noOptionsMessage={() => "Nenhum corretor encontrado"}
                  loadingMessage={() => "Carregando corretores..."}
                />
              </FormGroup>

              <FormGroup>
                <Label>Administrado por</Label>
                <StyledSelect
                  classNamePrefix="react-select"
                  value={brokers.find(broker => broker.id === property.managed_by_id) || null}
                  onChange={(selectedOption: any) => handleInputChange('managed_by_id', selectedOption?.id || undefined)}
                  options={brokers}
                  getOptionLabel={(option: any) => `${option.name} - ${option.creci}`}
                  getOptionValue={(option: any) => option.id.toString()}
                  formatOptionLabel={formatBrokerOption}
                  placeholder="Buscar corretor..."
                  isClearable
                  isSearchable
                  isLoading={loadingBrokers}
                  noOptionsMessage={() => "Nenhum corretor encontrado"}
                  loadingMessage={() => "Carregando corretores..."}
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>Proprietário do Imóvel</Label>
                <StyledSelect
                  classNamePrefix="react-select"
                  value={leads.find(lead => lead.id === property.owner_id) || null}
                  onChange={(selectedOption: any) => handleInputChange('owner_id', selectedOption?.id || undefined)}
                  options={leads}
                  getOptionLabel={(option: any) => {
                    const phone = option.phone || 'Sem telefone';
                    return `${option.name} - ${option.email} - ${phone}`;
                  }}
                  getOptionValue={(option: any) => option.id.toString()}
                  formatOptionLabel={formatLeadOption}
                  placeholder="Buscar proprietário por nome, email ou telefone..."
                  isClearable
                  isSearchable
                  isLoading={loadingLeads}
                  noOptionsMessage={() => "Nenhum cliente encontrado"}
                  loadingMessage={() => "Carregando clientes..."}
                  filterOption={(option: any, inputValue: any) => {
                    const searchStr = inputValue.toLowerCase();
                    return (
                      option.data.name.toLowerCase().includes(searchStr) ||
                      option.data.email.toLowerCase().includes(searchStr) ||
                      (option.data.phone && option.data.phone.includes(searchStr)) ||
                      (option.data.client_type && option.data.client_type.toLowerCase().includes(searchStr))
                    );
                  }}
                />
              </FormGroup>
            </FormRow>
          </Section>

          {/* Categorias */}
          <Section>
            <SectionTitle>Categorias</SectionTitle>
            
            <FormGroup>
              <Label>Selecione as categorias que se aplicam a esta propriedade</Label>
              <CategoriesContainer>
                {categories.map(category => (
                  <CategoryOption
                    key={category.id}
                    selected={property.category_ids?.includes(category.id) || false}
                    color={category.color}
                    onClick={() => toggleCategory(category.id)}
                  >
                    {category.icon && <CategoryIcon>{category.icon}</CategoryIcon>}
                    <CategoryName>{category.name}</CategoryName>
                  </CategoryOption>
                ))}
              </CategoriesContainer>
              {categories.length === 0 && (
                <p style={{ color: '#6b7280', fontStyle: 'italic' }}>
                  Nenhuma categoria disponível. Crie categorias na seção de configurações.
                </p>
              )}
            </FormGroup>
          </Section>

          {/* Localização */}
          <Section>
            <SectionTitle>Localização</SectionTitle>
            
            <FormRow>
              <FormGroup>
                <Label>CEP</Label>
                <Input
                  type="text"
                  value={formatZipCode(property.address?.zipCode || '')}
                  onChange={(e) => {
                    const zipCode = e.target.value.replace(/\D/g, '');
                    handleInputChange('address', {
                      ...property.address!,
                      zipCode
                    });
                    handleZipCodeSearch(zipCode);
                  }}
                  placeholder="00000-000"
                  maxLength={9}
                  inputMode="numeric"
                  required
                />
                {zipCodeLookupValid === false && (
                  <HelpText $tone="error">Informe um CEP válido com 8 dígitos.</HelpText>
                )}
              </FormGroup>

              <FormGroup>
                <Label>Rua/Avenida</Label>
                <Input
                  type="text"
                  value={property.address?.street || ''}
                  onChange={(e) => handleInputChange('address', {
                    ...property.address!,
                    street: e.target.value
                  })}
                  placeholder="Nome da rua"
                />
              </FormGroup>

              <FormGroup>
                <Label>Número</Label>
                <Input
                  type="text"
                  value={property.address?.number || ''}
                  onChange={(e) => handleInputChange('address', {
                    ...property.address!,
                    number: e.target.value
                  })}
                  placeholder="123"
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>Bairro</Label>
                <Input
                  type="text"
                  value={property.address?.neighborhood || ''}
                  onChange={(e) => handleInputChange('address', {
                    ...property.address!,
                    neighborhood: e.target.value
                  })}
                  placeholder="Nome do bairro"
                />
              </FormGroup>

              <FormGroup>
                <Label>Cidade</Label>
                <Input
                  type="text"
                  value={property.address?.city || ''}
                  onChange={(e) => handleInputChange('address', {
                    ...property.address!,
                    city: e.target.value
                  })}
                  placeholder="Nome da cidade"
                />
              </FormGroup>

              <FormGroup>
                <Label>Estado</Label>
                <NativeSelect
                  value={property.address?.state || ''}
                  onChange={(e) => handleInputChange('address', {
                    ...property.address!,
                    state: e.target.value
                  })}
                >
                  <option value="">Selecione</option>
                  <option value="AC">Acre</option>
                  <option value="AL">Alagoas</option>
                  <option value="AP">Amapá</option>
                  <option value="AM">Amazonas</option>
                  <option value="BA">Bahia</option>
                  <option value="CE">Ceará</option>
                  <option value="DF">Distrito Federal</option>
                  <option value="ES">Espírito Santo</option>
                  <option value="GO">Goiás</option>
                  <option value="MA">Maranhão</option>
                  <option value="MT">Mato Grosso</option>
                  <option value="MS">Mato Grosso do Sul</option>
                  <option value="MG">Minas Gerais</option>
                  <option value="PA">Pará</option>
                  <option value="PB">Paraíba</option>
                  <option value="PR">Paraná</option>
                  <option value="PE">Pernambuco</option>
                  <option value="PI">Piauí</option>
                  <option value="RJ">Rio de Janeiro</option>
                  <option value="RN">Rio Grande do Norte</option>
                  <option value="RS">Rio Grande do Sul</option>
                  <option value="RO">Rondônia</option>
                  <option value="RR">Roraima</option>
                  <option value="SC">Santa Catarina</option>
                  <option value="SP">São Paulo</option>
                  <option value="SE">Sergipe</option>
                  <option value="TO">Tocantins</option>
                </NativeSelect>
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label>Complemento (Opcional)</Label>
              <Input
                type="text"
                value={property.address?.complement || ''}
                onChange={(e) => handleInputChange('address', {
                  ...property.address!,
                  complement: e.target.value
                })}
                placeholder="Apartamento, bloco, etc."
              />
            </FormGroup>

            <FormGroup>
              <Label>Condomínio / empreendimento (Opcional)</Label>
              <Input
                type="text"
                value={property.condominium_name || ''}
                onChange={(e) => handleInputChange('condominium_name', e.target.value)}
                placeholder="Ex.: Buona Vita, Borghese Boulevard"
              />
              <HelpText>
                Use o nome comercial. O bairro continua sendo preenchido separadamente.
              </HelpText>
            </FormGroup>

            <FormGroup>
              <Label>Endereço Completo (Gerado automaticamente)</Label>
              <LocationContainer>
                <LocationInput
                  type="text"
                  value={property.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  placeholder="Endereço será preenchido automaticamente"
                  readOnly
                />
                <LocationButton
                  type="button"
                  onClick={handleLocationSearch}
                  title="Buscar no Google Maps"
                >
                  📍
                </LocationButton>
              </LocationContainer>
            </FormGroup>
          </Section>

          {/* Características */}
          <Section>
            <SectionTitle>Características</SectionTitle>
            
            <FormRow>
               {property.pricing_infos.some(item => item.business_type === 'SALE') && (
                 <FormGroup>
                   <Label>Preço de venda</Label>
                   <Input
                     type="text"
                     value={priceDisplay}
                     onChange={(e) => handlePriceChange('SALE', e.target.value)}
                     placeholder="R$ 0,00"
                     required
                   />
                 </FormGroup>
               )}

               {property.pricing_infos.some(item => item.business_type === 'RENTAL') && (
                 <FormGroup>
                   <Label>Valor da locação / mês</Label>
                   <Input
                     type="text"
                     value={rentalPriceDisplay}
                     onChange={(e) => handlePriceChange('RENTAL', e.target.value)}
                     placeholder="R$ 0,00"
                     required
                   />
                 </FormGroup>
               )}

               <FormGroup>
                 <Label>Área útil</Label>
                 <Input
                   type="text"
                   value={areaDisplay}
                   onChange={(e) => handleAreaChange(e.target.value)}
                   placeholder="0 m²"
                   required
                 />
                 <HelpText>É a área mostrada no site.</HelpText>
               </FormGroup>

               <FormGroup>
                 <Label>Área total</Label>
                 <Input
                   type="text"
                   value={totalAreaDisplay}
                   onChange={(e) => handleTotalAreaChange(e.target.value)}
                   placeholder="0 m²"
                 />
                 <HelpText>Usada pelo anúncio no Canal Pro. Em branco = não informada.</HelpText>
               </FormGroup>

               <FormGroup>
                 <Label>Condomínio (mensal)</Label>
                 <Input
                   type="text"
                   value={condoFeeDisplay}
                   onChange={(e) => handleCondoFeeChange(e.target.value)}
                   placeholder="R$ 0,00"
                 />
               </FormGroup>

               <FormGroup>
                 <Label>IPTU</Label>
                 <Input
                   type="text"
                   value={iptuDisplay}
                   onChange={(e) => handleIptuChange(e.target.value)}
                   placeholder="R$ 0,00"
                 />
               </FormGroup>

               <FormGroup>
                 <Label>Período do IPTU</Label>
                 <NativeSelect
                   value={property.iptu_period || 'MONTHLY'}
                   onChange={(e) => {
                     const period = e.target.value as 'MONTHLY' | 'YEARLY';
                     const currentValue = property.yearly_iptu || property.iptu || 0;
                     setProperty(prev => ({
                       ...prev,
                       iptu_period: period,
                       iptu: period === 'MONTHLY' ? currentValue : undefined,
                       yearly_iptu: period === 'YEARLY' ? currentValue : undefined,
                       pricing_infos: prev.pricing_infos.map(item => ({
                         ...item,
                         yearly_iptu: period === 'YEARLY' ? currentValue : undefined,
                       })),
                     }));
                   }}
                 >
                   <option value="MONTHLY">Mensal</option>
                   <option value="YEARLY">Anual</option>
                 </NativeSelect>
               </FormGroup>
            </FormRow>

            <FormRow>
               <FormGroup>
                 <Label>Quartos</Label>
                 <Input
                   type="number"
                   min="0"
                   value={property.bedrooms || ''}
                   onChange={(e) => handleInputChange('bedrooms', parseInt(e.target.value) || 0)}
                   placeholder="0"
                 />
               </FormGroup>

               <FormGroup>
                 <Label>Suítes</Label>
                 <Input
                   type="number"
                   min="0"
                   value={property.suites ?? ''}
                   onChange={(e) => handleInputChange('suites', e.target.value === '' ? undefined : Number(e.target.value))}
                   placeholder="0"
                 />
               </FormGroup>

               <FormGroup>
                 <Label>Banheiros</Label>
                 <Input
                   type="number"
                   min="0"
                   value={property.bathrooms || ''}
                   onChange={(e) => handleInputChange('bathrooms', parseInt(e.target.value) || 0)}
                   placeholder="0"
                 />
               </FormGroup>

               <FormGroup>
                 <Label>Vagas de garagem</Label>
                 <Input
                   type="number"
                   min="0"
                   value={property.parking_spaces ?? ''}
                   onChange={(e) => handleInputChange('parking_spaces', e.target.value === '' ? undefined : Number(e.target.value))}
                   placeholder="0"
                 />
               </FormGroup>

               <FormGroup>
                 <Label>Andar da unidade</Label>
                 <Input
                   type="number"
                   min="0"
                   value={property.unit_floor ?? ''}
                   onChange={(e) => handleInputChange('unit_floor', e.target.value === '' ? undefined : Number(e.target.value))}
                   placeholder="Não informado"
                 />
               </FormGroup>

               <FormGroup>
                 <Label>Pavimentos</Label>
                 <Input
                   type="number"
                   min="0"
                   value={property.floors ?? ''}
                   onChange={(e) => handleInputChange('floors', e.target.value === '' ? undefined : Number(e.target.value))}
                   placeholder="Não informado"
                 />
               </FormGroup>

               <FormGroup>
                 <Label>Torres ou blocos</Label>
                 <Input
                   type="number"
                   min="0"
                   value={property.buildings ?? ''}
                   onChange={(e) => handleInputChange('buildings', e.target.value === '' ? undefined : Number(e.target.value))}
                   placeholder="Não informado"
                 />
               </FormGroup>

               <FormGroup>
                 <Label>Unidades por andar</Label>
                 <Input
                   type="number"
                   min="0"
                   value={property.units_on_the_floor ?? ''}
                   onChange={(e) => handleInputChange('units_on_the_floor', e.target.value === '' ? undefined : Number(e.target.value))}
                   placeholder="Não informado"
                 />
               </FormGroup>
             </FormRow>
          </Section>

          {/* Características Adicionais */}
          <Section>
            <SectionTitle>Características Adicionais</SectionTitle>
            
            <FormGroup>
              <Label>Características</Label>
              <FeaturesContainer>
                {property.features.map((feature, index) => (
                  <FeatureTag key={index}>
                    {feature}
                    <RemoveFeature
                      type="button"
                      onClick={() => removeFeature(feature)}
                    >
                      ×
                    </RemoveFeature>
                  </FeatureTag>
                ))}
              </FeaturesContainer>
              
              <AddFeatureContainer>
                <AddFeatureInput
                  type="text"
                  value={newFeature}
                  onChange={(e) => setNewFeature(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Digite uma característica (ex: Piscina, Garagem, etc.)"
                />
                <AddFeatureButton
                  type="button"
                  onClick={addFeature}
                >
                  Adicionar
                </AddFeatureButton>
              </AddFeatureContainer>
            </FormGroup>
          </Section>

          {/* Imagens */}
          <Section>
            <SectionTitle>Imagens</SectionTitle>
            <ImageGalleryWithCover
              images={property.images}
              coverImage={property.cover_image}
              onImagesChange={(images) => handleInputChange('images', images)}
              onCoverImageChange={(coverImage) => handleInputChange('cover_image', coverImage)}
              onUploadingChange={setImagesUploading}
            />
          </Section>

          {/* Vídeos */}
          <Section>
            <SectionTitle>Vídeos</SectionTitle>
            <VideoManager
              videos={property.videos || []}
              onVideosChange={(videos) => handleInputChange('videos', videos)}
              featuredVideoId={property.featured_video_id}
              onFeaturedChange={(videoId) => handleInputChange('featured_video_id', videoId)}
            />
          </Section>

          {/* Informações Internas */}
          <Section style={{ background: '#fef3c7', border: '1px solid #fbbf24' }}>
            <SectionTitle style={{ color: '#92400e' }}>
              🔒 Informações Internas (não visíveis ao cliente)
            </SectionTitle>
            
            <FormGroup>
              <Label>Observações Internas</Label>
              <TextArea
                value={property.internal_observations || ''}
                onChange={(e) => handleInputChange('internal_observations', e.target.value)}
                placeholder="Anotações internas sobre o imóvel, histórico, observações importantes..."
                rows={4}
              />
            </FormGroup>

            <FormRow>
              <FormGroup>
                <Label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={property.has_keys || false}
                    onChange={(e) => handleInputChange('has_keys', e.target.checked)}
                    style={{ width: 'auto', marginRight: '0.5rem' }}
                  />
                  Possui chaves do imóvel
                </Label>
              </FormGroup>

              <FormGroup>
                <Label>Localização das chaves</Label>
                <Input
                  type="text"
                  value={property.keys_location || ''}
                  onChange={(e) => handleInputChange('keys_location', e.target.value)}
                  placeholder="Ex: Gaveta 3 do armário de chaves"
                  disabled={!property.has_keys}
                />
              </FormGroup>
            </FormRow>
          </Section>

          <ButtonGroup>
            <Button
              type="button"
              onClick={() => {
                setIsModalOpen(false);
                navigate('/admin/properties');
              }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              $variant="primary"
              disabled={loading || imagesUploading}
            >
              {imagesUploading ? 'Enviando imagens...' : loading ? 'Salvando...' : (id ? 'Atualizar' : 'Criar')}
            </Button>
          </ButtonGroup>
        </Form>
      </Modal>
    </Container>
  );
};

export default AdminPropertyForm; 
